import io from 'socket.io-client';
import axios from 'axios';
import soundNotification from '../utils/soundNotification';

/**
 * ChatService - Handles all chat-related API calls and Socket.io connections
 * Matches the Flutter ChatService functionality
 * Adapted for CareTap Transportation frontend
 */
class ChatService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    // Use environment variables or default values
    // Updated to match Flutter backend on port 8001
    // Use REACT_APP_CHAT_API_URL if available, otherwise construct from REACT_APP_API_BASE_URL
    // Fix: Remove /services suffix if present, as chat API is at /api/chat, not /services/api/chat
    let baseUrl = process.env.REACT_APP_API_BASE_URL;
    if (baseUrl) {
      // Remove trailing /services if it exists
      baseUrl = baseUrl.replace(/\/services\/?$/, '');
      // Ensure no trailing slash before appending /api/chat
      baseUrl = baseUrl.replace(/\/$/, '');
    }
    
    // Use environment variables, fallback to localhost for development
    // IMPORTANT: Set REACT_APP_CHAT_API_URL and REACT_APP_SOCKET_URL in .env file
    this.apiUrl = process.env.REACT_APP_CHAT_API_URL || 
                  (baseUrl ? `${baseUrl}/api/chat` : 'http://localhost:8001/api/chat');
    this.socketUrl = process.env.REACT_APP_SOCKET_URL || 'http://localhost:8001';
    
    // Validate API URL
    if (!this.apiUrl || this.apiUrl === 'undefined' || this.apiUrl.includes('undefined')) {
      // Invalid API URL - silently fail
    }
    
    // Listeners
    this.messageListeners = new Map();
    this.typingListeners = new Map();
    this.onlineStatusListeners = [];
    this.messageReadStatusListeners = new Map();
    this.globalMessageListeners = [];
    this.roomUpdateListeners = [];
    this.messageDeletedListeners = new Map();
    this.chatClearedListeners = new Map();
    this.messageDeletedListenerSet = false;
    this.chatClearedListenerSet = false;
  }

  /**
   * Connect to Socket.io server
   * @param {Object} userInfo - User information
   * @param {string} userInfo.userId - User ID
   * @param {string} userInfo.userName - User name
   * @param {string} userInfo.userRole - User role (admin, officeAdmin, driver)
   * @param {string} userInfo.agencyId - Agency ID
   */
  connect(userInfo) {
    // If already connected with the same user info, don't reconnect
    if (this.socket && this.isConnected) {
      return;
    }

    // Disconnect existing socket if any (to prevent duplicate connections)
    if (this.socket) {
      this.socket.removeAllListeners(); // Remove all listeners to prevent duplicates
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }

    // Validate socket URL
    if (!this.socketUrl || this.socketUrl === 'undefined') {
      return;
    }

    try {
      this.socket = io(this.socketUrl, {
        transports: ['websocket', 'polling'],
        autoConnect: true,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        timeout: 20000, // 20 second timeout
      });

      // Store userInfo for reconnection
      this.currentUserInfo = userInfo;

      this.socket.on('connect', () => {
        this.isConnected = true;

        // Join with user details
        this.socket.emit('user:join', {
          userId: userInfo.userId,
          userName: userInfo.userName,
          userRole: userInfo.userRole,
          agencyId: userInfo.agencyId,
        });
      });

      this.socket.on('disconnect', () => {
        this.isConnected = false;
      });

      this.socket.on('connect_error', (error) => {
        // Socket connection error - silently handle
      });

      this.socket.on('error', (error) => {
        // Socket error - silently handle
      });

      // Listen to incoming messages (only register once per socket instance)
      this.socket.on('message:received', (data) => {
        try {
          const message = this._parseMessage(data);
          this._notifyMessageListeners(message);
          // Sound notification is handled in _notifyMessageListeners
        } catch (e) {
          // Error parsing message - silently handle
        }
      });

      // Listen to typing status
      this.socket.on('user:typing_status', (data) => {
        this._notifyTypingListeners(data);
      });

      // Listen to online users
      this.socket.on('users:online', (data) => {
        if (Array.isArray(data)) {
          data.forEach(user => {
            this._notifyOnlineStatusListeners({
              userId: user.userId,
              userName: user.userName,
              userRole: user.userRole,
              online: true,
            });
          });
        }
      });

      this.socket.on('user:online', (data) => {
        // Ensure online is set to true
        this._notifyOnlineStatusListeners({
          ...data,
          online: true
        });
      });

      this.socket.on('user:offline', (data) => {
        // Ensure online is set to false
        this._notifyOnlineStatusListeners({
          ...data,
          online: false
        });
      });

      // Listen to message read status
      this.socket.on('message:read_status', (data) => {
        this._notifyMessageReadStatusListeners(data);
      });

      // Listen to room updates
      this.socket.on('room:updated', (data) => {
        this._notifyRoomUpdateListeners(data);
      });

      this.socket.connect();
    } catch (e) {
      throw e;
    }
  }

  /**
   * Disconnect from Socket.io
   */
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.messageListeners.clear();
      this.typingListeners.clear();
      this.onlineStatusListeners = [];
      this.messageReadStatusListeners.clear();
      this.globalMessageListeners = [];
      this.roomUpdateListeners = [];
    }
  }

  /**
   * Join a specific chat room
   */
  joinRoom(roomId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('room:join', { roomId });
    }
  }

  /**
   * Send a message
   * Returns a promise that resolves when message is sent
   * Uses socket if connected, otherwise falls back to REST API
   */
  async sendMessage(messageData) {
    // Try socket first if connected
    if (this.socket && this.isConnected) {
      try {
        this.socket.emit('message:send', {
          roomId: messageData.roomId,
          senderId: messageData.senderId,
          senderName: messageData.senderName,
          senderRole: messageData.senderRole,
          receiverId: messageData.receiverId,
          receiverName: messageData.receiverName,
          receiverRole: messageData.receiverRole,
          message: messageData.message,
          messageType: messageData.messageType || 'text',
          fileUrl: messageData.fileUrl || null,
          fileName: messageData.fileName || null,
          fileSize: messageData.fileSize || null,
          fileType: messageData.fileType || null,
          duration: messageData.duration || null,
        });
        
        // Return success immediately for socket (optimistic)
        return {
          success: true,
          method: 'socket',
          data: messageData
        };
      } catch (error) {
        // Fall through to REST API fallback
      }
    }
    
    // Fallback to REST API if socket not connected or failed
    try {
      const response = await axios.post(`${this.apiUrl}/messages`, {
        roomId: messageData.roomId,
        senderId: messageData.senderId,
        senderName: messageData.senderName,
        senderRole: messageData.senderRole,
        receiverId: messageData.receiverId,
        receiverName: messageData.receiverName,
        receiverRole: messageData.receiverRole,
        message: messageData.message,
        messageType: messageData.messageType || 'text',
        fileUrl: messageData.fileUrl || null,
        fileName: messageData.fileName || null,
        fileSize: messageData.fileSize || null,
        fileType: messageData.fileType || null,
        duration: messageData.duration || null,
      });
      
      return {
        success: true,
        method: 'rest',
        data: response.data
      };
    } catch (error) {
      throw new Error('Failed to send message. Please check your connection and try again.');
    }
  }

  /**
   * Mark messages as read
   */
  markMessagesAsRead(roomId, userId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('message:read', { roomId, userId });
    }
  }

  /**
   * Send typing indicator
   */
  sendTypingIndicator({ roomId, userId, userName, isTyping }) {
    if (this.socket && this.isConnected) {
      this.socket.emit('user:typing', {
        roomId,
        userId,
        userName,
        isTyping,
      });
    }
  }

  /**
   * Request list of online users
   * Matches Flutter: emit('request:online_users')
   */
  requestOnlineUsers() {
    if (this.socket && this.isConnected) {
      // Try both event names for compatibility
      this.socket.emit('request:online_users');
      // Also try the old event name as fallback
      this.socket.emit('users:request_online');
    }
  }

  /**
   * Notify server that user is online
   */
  notifyUserOnline(userInfo) {
    if (this.socket && this.isConnected) {
      this.socket.emit('user:online', {
        userId: userInfo.userId,
        userName: userInfo.userName,
        userRole: userInfo.userRole,
        agencyId: userInfo.agencyId,
      });
    }
  }

  /**
   * Notify server that user is offline
   */
  notifyUserOffline(userInfo) {
    if (this.socket && this.isConnected) {
      this.socket.emit('user:offline', {
        userId: userInfo.userId,
        userName: userInfo.userName,
        userRole: userInfo.userRole,
        agencyId: userInfo.agencyId,
      });
    }
  }

  /**
   * Notify server that user is viewing a room
   */
  notifyViewingRoom(roomId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('room:viewing', { roomId });
    }
  }

  /**
   * Notify server that user is no longer viewing a room
   */
  notifyNotViewingRoom(roomId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('room:not_viewing', { roomId });
    }
  }

  /**
   * Register message deleted listener
   */
  onMessageDeleted(roomId, callback) {
    // Store listener for cleanup
    if (!this.messageDeletedListeners) {
      this.messageDeletedListeners = new Map();
    }
    this.messageDeletedListeners.set(roomId, callback);
    
    // Set up socket listener if not already set
    if (this.socket && !this.messageDeletedListenerSet) {
      this.socket.on('message:deleted', (data) => {
        const listener = this.messageDeletedListeners?.get(data.roomId);
        if (listener) {
          listener(data);
        }
      });
      this.messageDeletedListenerSet = true;
    }
    
    return () => this.removeMessageDeletedListener(roomId);
  }

  /**
   * Register chat cleared listener
   */
  onChatCleared(roomId, callback) {
    // Store listener for cleanup
    if (!this.chatClearedListeners) {
      this.chatClearedListeners = new Map();
    }
    this.chatClearedListeners.set(roomId, callback);
    
    // Set up socket listener if not already set
    if (this.socket && !this.chatClearedListenerSet) {
      this.socket.on('chat:cleared', (data) => {
        const listener = this.chatClearedListeners?.get(data.roomId);
        if (listener) {
          listener(data);
        }
      });
      this.chatClearedListenerSet = true;
    }
    
    return () => this.removeChatClearedListener(roomId);
  }

  /**
   * Remove message deleted listener
   */
  removeMessageDeletedListener(roomId) {
    if (this.messageDeletedListeners) {
      this.messageDeletedListeners.delete(roomId);
    }
  }

  /**
   * Remove chat cleared listener
   */
  removeChatClearedListener(roomId) {
    if (this.chatClearedListeners) {
      this.chatClearedListeners.delete(roomId);
    }
  }

  /**
   * Get file base URL for file URL resolution
   */
  getFileBaseUrl() {
    return this.socketUrl || this.apiUrl?.replace('/api/chat', '') || 'http://localhost:8001';
  }

  /**
   * Get authenticated file URL (for files requiring auth)
   */
  async getAuthenticatedFileUrl(fileUrl) {
    try {
      const token = localStorage.getItem('token') && localStorage.getItem('token') !== 'undefined' 
        ? JSON.parse(localStorage.getItem('token')) 
        : '';
      
      if (!token) {
        return fileUrl;
      }

      const response = await fetch(fileUrl, {
        method: 'GET',
        headers: {
          'Authorization': token
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        return URL.createObjectURL(blob);
      }
    } catch (error) {
      console.warn('Failed to get authenticated file URL:', error);
    }
    return fileUrl;
  }

  /**
   * Register message listener for a room
   */
  onMessage(roomId, callback) {
    this.messageListeners.set(roomId, callback);
  }

  /**
   * Register typing listener for a room
   */
  onTyping(roomId, callback) {
    this.typingListeners.set(roomId, callback);
  }

  /**
   * Register online status listener
   */
  onOnlineStatus(callback) {
    this.onlineStatusListeners.push(callback);
  }

  /**
   * Register global message listener (for chat list updates)
   */
  onGlobalMessage(callback) {
    this.globalMessageListeners.push(callback);
    // Return a cleanup function
    return () => this.removeGlobalMessageListener(callback);
  }

  /**
   * Register room update listener
   */
  onRoomUpdate(callback) {
    this.roomUpdateListeners.push(callback);
    // Return a cleanup function
    return () => this.removeRoomUpdateListener(callback);
  }

  /**
   * Register message read status listener for a room
   */
  onMessageReadStatus(roomId, callback) {
    this.messageReadStatusListeners.set(roomId, callback);
    // Return cleanup function
    return () => this.removeMessageReadStatusListener(roomId);
  }

  /**
   * Remove listeners
   */
  removeMessageListener(roomId) {
    this.messageListeners.delete(roomId);
  }

  removeTypingListener(roomId) {
    this.typingListeners.delete(roomId);
  }

  removeMessageReadStatusListener(roomId) {
    this.messageReadStatusListeners.delete(roomId);
  }

  /**
   * Remove global message listener
   */
  removeGlobalMessageListener(callback) {
    const index = this.globalMessageListeners.indexOf(callback);
    if (index > -1) {
      this.globalMessageListeners.splice(index, 1);
    }
  }

  /**
   * Remove room update listener
   */
  removeRoomUpdateListener(callback) {
    const index = this.roomUpdateListeners.indexOf(callback);
    if (index > -1) {
      this.roomUpdateListeners.splice(index, 1);
    }
  }

  /**
   * Remove online status listener
   */
  removeOnlineStatusListener(callback) {
    const index = this.onlineStatusListeners.indexOf(callback);
    if (index > -1) {
      this.onlineStatusListeners.splice(index, 1);
    }
  }

  /**
   * Parse rooms response - EXACTLY matches Flutter parsing logic
   * Flutter code:
   *   final data = jsonDecode(response.body);
   *   List<dynamic> roomsList;
   *   if (data is List) { roomsList = data; }
   *   else if (data is Map && data.containsKey('data')) {
   *     if (data['data'] is List) { roomsList = data['data'] as List; }
   *   }
   *   final rooms = <ChatRoom>[];
   *   for (var i = 0; i < roomsList.length; i++) {
   *     rooms.add(ChatRoom.fromJson(roomsList[i] as Map<String, dynamic>));
   *   }
   */
  _parseRoomsResponse(responseData) {
    let roomsList = [];
    
    // Format 1: Direct array (Flutter: if (data is List) { roomsList = data; })
    if (Array.isArray(responseData)) {
      roomsList = responseData;
    }
    // Format 2: {success: true, data: [...]} or {data: [...]} - matches Flutter
    // Flutter: else if (data is Map && data.containsKey('data')) { if (data['data'] is List) { roomsList = data['data'] as List; } }
    else if (responseData && typeof responseData === 'object' && responseData.data) {
      if (Array.isArray(responseData.data)) {
        roomsList = responseData.data;
      } else {
        console.warn('⚠️ Expected List but got:', typeof responseData.data);
        return [];
      }
    } else {
      console.warn('⚠️ Unexpected response format');
      return [];
    }
    
    // Flutter: Parse rooms, skipping invalid entries
    // for (var i = 0; i < roomsList.length; i++) {
    //   try { rooms.add(ChatRoom.fromJson(roomsList[i] as Map<String, dynamic>)); }
    //   catch (e) { print('⚠️ Skipping invalid room at index $i: $e'); }
    // }
    const rooms = [];
    for (let i = 0; i < roomsList.length; i++) {
      try {
        // Just return the raw room objects (parsing will happen in component)
        if (roomsList[i] && typeof roomsList[i] === 'object') {
          rooms.push(roomsList[i]);
        } else {
          console.warn(`⚠️ Skipping invalid room at index ${i}: ${roomsList[i]} (type: ${typeof roomsList[i]})`);
        }
      } catch (e) {
        console.warn(`⚠️ Skipping invalid room at index ${i}:`, e);
      }
    }
    
    return rooms;
  }

  /**
   * Get authorization header
   * Updated to use existing project's token storage
   */
  _getAuthHeaders() {
    // Use the existing project's token storage (matches App.js pattern)
    const token = localStorage.getItem('token') && localStorage.getItem('token') !== 'undefined' 
      ? JSON.parse(localStorage.getItem('token')) 
      : '';
    const headers = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['authorization'] = token;
    }
    return headers;
  }

  /**
   * Normalize admin user ID to prevent duplicate rooms
   * Converts admin userId "1" to "admin_{agencyId}" format
   * This ensures consistent room IDs regardless of who creates the room
   */
  _normalizeAdminId(user) {
    if (!user) return user;
    
    // If user is admin and userId doesn't start with "admin_", normalize it
    if (user.userRole === 'admin' && user.userId && !user.userId.toString().startsWith('admin_')) {
      const agencyId = user.agencyId || '';
      if (agencyId) {
        return {
          ...user,
          userId: `admin_${agencyId}`
        };
      }
    }
    return user;
  }

  /**
   * Deduplicate rooms that represent the same conversation
   * Handles cases where both "1_302" and "302_admin_CT123" exist
   * Always keeps the room with normalized admin ID format
   */
  _deduplicateRooms(rooms, currentUserId, agencyId, userRole) {
    if (!rooms || rooms.length === 0) return rooms;
    
    const roomMap = new Map();
    const adminFormatId = `admin_${agencyId}`;
    const duplicatesFound = [];
    
    rooms.forEach(room => {
      if (!room.participants || room.participants.length !== 2) {
        // Keep rooms that don't have exactly 2 participants as-is
        if (!roomMap.has(room.roomId)) {
          roomMap.set(room.roomId, room);
        }
        return;
      }
      
      // Normalize participant IDs for comparison
      const normalizedParticipants = room.participants.map(p => {
        const pUserId = String(p.userId || p.user_id || '');
        // If participant is admin, normalize to admin_{agencyId} format
        if (p.userRole === 'admin' && !pUserId.startsWith('admin_')) {
          return adminFormatId;
        }
        // Also handle case where userId might be stored as number
        return pUserId;
      }).sort().join('_');
      
      // Check if we already have a room with these normalized participants
      let existingRoom = null;
      let existingRoomId = null;
      
      for (const [roomId, r] of roomMap.entries()) {
        if (!r.participants || r.participants.length !== 2) continue;
        
        const rNormalized = r.participants.map(p => {
          const pUserId = String(p.userId || p.user_id || '');
          if (p.userRole === 'admin' && !pUserId.startsWith('admin_')) {
            return adminFormatId;
          }
          return pUserId;
        }).sort().join('_');
        
        if (rNormalized === normalizedParticipants) {
          existingRoom = r;
          existingRoomId = roomId;
          break;
        }
      }
      
      if (existingRoom) {
        // We have a duplicate - keep the one with normalized admin ID format
        const currentRoomId = room.roomId || '';
        
        // Prefer room ID that contains "admin_" format
        const currentHasNormalized = currentRoomId.includes('admin_');
        const existingHasNormalized = existingRoomId.includes('admin_');
        
        if (currentHasNormalized && !existingHasNormalized) {
          // Current room has normalized format, replace existing
          roomMap.delete(existingRoomId);
          roomMap.set(currentRoomId, room);
          duplicatesFound.push({ removed: existingRoomId, kept: currentRoomId });
        } else if (!currentHasNormalized && existingHasNormalized) {
          // Existing room has normalized format, keep it and skip current
          duplicatesFound.push({ removed: currentRoomId, kept: existingRoomId });
        } else {
          // Both have same format, prefer the one with newer lastMessageTime
          const currentTime = new Date(room.lastMessageTime || 0).getTime();
          const existingTime = new Date(existingRoom.lastMessageTime || 0).getTime();
          
          if (currentTime > existingTime) {
            roomMap.delete(existingRoomId);
            roomMap.set(currentRoomId, room);
            duplicatesFound.push({ removed: existingRoomId, kept: currentRoomId, reason: 'newer message' });
          } else {
            duplicatesFound.push({ removed: currentRoomId, kept: existingRoomId, reason: 'newer message' });
          }
        }
      } else {
        // No duplicate found, add to map
        roomMap.set(room.roomId, room);
      }
    });
    
    const deduplicatedRooms = Array.from(roomMap.values());
    
    return deduplicatedRooms;
  }

  /**
   * REST API: Get or create chat room
   * FIXED: Normalizes admin IDs and ensures consistent room IDs
   * 
   * Issue: When Office Admin creates chat with Admin using userId "1",
   *        and Admin creates chat using userId "admin_CT123",
   *        two different rooms are created: "1_302" and "302_admin_CT123"
   * 
   * Fix: 
   * 1. Normalize both users' admin IDs to "admin_{agencyId}" format
   * 2. Always sort user IDs alphabetically before sending to backend
   *    This ensures room ID is always the same regardless of who initiates
   */
  async getOrCreateRoom(user1, user2) {
    try {
      // CRITICAL FIX: Normalize admin IDs to prevent duplicate rooms
      // If either user is admin with userId "1", convert to "admin_{agencyId}"
      const normalizedUser1 = this._normalizeAdminId(user1);
      const normalizedUser2 = this._normalizeAdminId(user2);
      
      // CRITICAL: Sort user IDs alphabetically to ensure consistent room IDs
      // This ensures that whether Admin creates chat with Driver or Driver creates with Admin,
      // the room ID will always be the same (e.g., "302_admin_CT123" not "admin_CT123_302")
      const userId1 = String(normalizedUser1?.userId || '');
      const userId2 = String(normalizedUser2?.userId || '');
      
      // Sort IDs alphabetically
      const sortedIds = [userId1, userId2].sort();
      const firstUserId = sortedIds[0];
      const secondUserId = sortedIds[1];
      
      // Determine which user object corresponds to which sorted ID
      let finalUser1, finalUser2;
      if (userId1 === firstUserId) {
        finalUser1 = normalizedUser1;
        finalUser2 = normalizedUser2;
      } else {
        finalUser1 = normalizedUser2;
        finalUser2 = normalizedUser1;
      }
      
      const response = await axios.post(
        `${this.apiUrl}/rooms`,
        { user1: finalUser1, user2: finalUser2 },
        { headers: this._getAuthHeaders() }
      );

      if (response.data?.data) {
        const room = response.data.data;
        return room;
      }
      console.warn('⚠️ getOrCreateRoom: Response format unexpected, returning raw response.data');
      return response.data;
    } catch (error) {
      return null;
    }
  }

  /**
   * REST API: Get all chat rooms for a user
   * EXACTLY matches Flutter ChatService.getChatRooms() implementation
   */
  async getChatRooms(userId, agencyId, userRole = null) {
    try {
      // Build URL EXACTLY like Flutter:
      // String url = '$apiUrl/rooms/$userId?agencyId=$agencyId';
      // if (userRole != null) { url += '&userRole=$userRole'; }
      let url = `${this.apiUrl}/rooms/${userId}?agencyId=${agencyId}`;
      if (userRole != null && userRole !== '') {
        // Flutter ALWAYS includes userRole if provided
        url += `&userRole=${userRole}`;
      }

      const headers = this._getAuthHeaders();
      
      const startTime = Date.now();
      const response = await axios.get(url, {
        headers: headers,
      });
      const duration = Date.now() - startTime;
      
      // Store API response for debugging (will be accessible from component)
      if (window.__CHAT_API_DEBUG__) {
        window.__CHAT_API_DEBUG__.lastResponse = response?.data;
        window.__CHAT_API_DEBUG__.lastUrl = url;
      } else {
        window.__CHAT_API_DEBUG__ = {
          lastResponse: response?.data,
          lastUrl: url
        };
      }

      // Parse response EXACTLY like Flutter:
      // Flutter: final data = jsonDecode(response.body);
      // Flutter: List<dynamic> roomsList; if (data is List) { roomsList = data; } else if (data is Map && data.containsKey('data')) { roomsList = data['data'] as List; }
      let rooms = this._parseRoomsResponse(response.data);
      
      // CRITICAL FIX: Deduplicate rooms with same participants but different IDs
      // This handles cases where both "1_302" and "302_admin_CT123" exist
      rooms = this._deduplicateRooms(rooms, userId, agencyId, userRole);
      
      return rooms;
    } catch (error) {
      return [];
    }
  }

  /**
   * REST API: Get messages for a room
   */
  async getMessages(roomId, limit = 50, skip = 0) {
    try {
      const response = await axios.get(
        `${this.apiUrl}/messages/${roomId}?limit=${limit}&skip=${skip}`,
        { headers: this._getAuthHeaders() }
      );

      if (response.data?.data && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      return [];
    } catch (error) {
      return [];
    }
  }

  /**
   * REST API: Mark messages as read
   */
  async markMessagesAsReadAPI(roomId, userId) {
    try {
      await axios.put(
        `${this.apiUrl}/messages/read/${roomId}`,
        { userId },
        { headers: this._getAuthHeaders() }
      );
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * REST API: Get unread count
   */
  async getUnreadCount(userId) {
    try {
      const response = await axios.get(
        `${this.apiUrl}/unread/${userId}`,
        { headers: this._getAuthHeaders() }
      );

      if (response.data?.data?.unreadCount !== undefined) {
        return response.data.data.unreadCount;
      }
      return 0;
    } catch (error) {
      return 0;
    }
  }

  /**
   * Upload file to server
   * @param {File} file - File to upload
   * @param {number} duration - Duration for audio/video files (optional)
   * @param {string} roomId - Room ID for file storage
   * @returns {Promise<Object>} Upload result with fileUrl, fileName, etc.
   */
  async uploadFile(file, duration = null, roomId = null) {
    try {
      if (!file) {
        return { success: false, useMessagesEndpoint: true };
      }

      if (!roomId) {
        return { success: false, useMessagesEndpoint: true };
      }

      // Create FormData
      const formData = new FormData();
      formData.append('file', file);
      formData.append('roomId', roomId);
      if (duration !== null) {
        formData.append('duration', duration.toString());
      }

      // Determine message type - CRITICAL: Check filename FIRST for voice recordings
      // Some browsers may set video/webm for webm files, but voice_ files are ALWAYS audio
      let messageType = 'file';
      const fileName = file.name || '';
      const fileType = file.type || '';
      
      // FIRST: Check if it's a voice recording by filename (takes highest priority)
      if (fileName.startsWith('voice_') || fileName.match(/\.(webm|m4a|ogg|wav|mp3)$/i)) {
        messageType = 'audio';
      } else if (fileType.startsWith('image/')) {
        messageType = 'image';
      } else if (fileType.startsWith('audio/')) {
        messageType = 'audio';
      } else if (fileType.startsWith('video/')) {
        // Only set as video if it's NOT a voice recording
        messageType = 'video';
      }

      formData.append('messageType', messageType);

      // Try upload endpoint
      try {
        const uploadUrl = `${this.apiUrl}/upload`;

        // CRITICAL: Don't set Content-Type manually for FormData - browser needs to set it with boundary
        const uploadHeaders = { ...this._getAuthHeaders() };
        // Remove Content-Type from headers - let browser set it automatically for FormData
        delete uploadHeaders['Content-Type'];
        delete uploadHeaders['content-type'];

        const response = await axios.post(uploadUrl, formData, {
          headers: uploadHeaders,
          timeout: 60000, // 60 second timeout for large files
        });

        if (response.data && response.data.success !== false) {
          const fileUrl = response.data.fileUrl || response.data.data?.fileUrl;
          const fileName = response.data.fileName || response.data.data?.fileName || file.name;
          const fileSize = response.data.fileSize || response.data.data?.fileSize || file.size;
          const fileType = response.data.fileType || response.data.data?.fileType || file.type;

          if (fileUrl) {
            return {
              success: true,
              useMessagesEndpoint: false,
              fileUrl: fileUrl,
              fileName: fileName,
              fileSize: fileSize,
              fileType: fileType,
              messageType: messageType
            };
          } else {
            console.warn('⚠️ Upload response missing fileUrl, falling back to messages endpoint');
            return { success: false, useMessagesEndpoint: true };
          }
        } else {
          console.warn('⚠️ Upload endpoint returned success: false, falling back to messages endpoint');
          return { success: false, useMessagesEndpoint: true };
        }
      } catch (uploadError) {
        console.warn('⚠️ Upload endpoint not available or failed:', uploadError.message || 'Unknown error');
        
        // Safely access error response
        if (uploadError.response) {
          console.warn('⚠️ Upload error response:', {
            status: uploadError.response.status,
            data: uploadError.response.data
          });
        } else if (uploadError.request) {
          console.warn('⚠️ Upload request failed - no response from server');
        } else {
          console.warn('⚠️ Upload error:', uploadError.message || 'Unknown error');
        }
        
        // Fallback to messages endpoint
        return { success: false, useMessagesEndpoint: true };
      }
    } catch (error) {
      return { success: false, useMessagesEndpoint: true };
    }
  }

  /**
   * Send file message via /messages endpoint (fallback when upload endpoint not available)
   * @param {Object} messageData - Message data including file
   * @returns {Promise<Object>} Message response
   */
  async sendFileMessageViaAPI(messageData) {
    try {
      // Validate required fields
      if (!messageData.file) {
        throw new Error('File is required to send file message');
      }
      if (!messageData.roomId) {
        throw new Error('Room ID is required to send file message');
      }

      const formData = new FormData();
      formData.append('roomId', messageData.roomId);
      formData.append('senderId', messageData.senderId);
      formData.append('senderName', messageData.senderName);
      formData.append('senderRole', messageData.senderRole);
      formData.append('receiverId', messageData.receiverId);
      formData.append('receiverName', messageData.receiverName);
      formData.append('receiverRole', messageData.receiverRole);
      formData.append('message', messageData.message || '');
      formData.append('file', messageData.file);
      
      if (messageData.duration !== null && messageData.duration !== undefined) {
        formData.append('duration', messageData.duration.toString());
      }

      // Determine message type - CRITICAL: Check file name for voice recordings
      let messageType = 'file';
      if (messageData.file) {
        const fileName = messageData.file.name || '';
        const fileType = messageData.file.type || '';
        
        // First check if it's a voice recording by filename
        if (fileName.startsWith('voice_') || fileName.match(/\.(webm|m4a|ogg|wav|mp3)$/i)) {
          messageType = 'audio';
        } else if (fileType.startsWith('image/')) {
          messageType = 'image';
        } else if (fileType.startsWith('audio/')) {
          messageType = 'audio';
        } else if (fileType.startsWith('video/')) {
          messageType = 'video';
        }
      }
      
      // Use messageType from messageData if provided (takes precedence)
      if (messageData.messageType) {
        messageType = messageData.messageType;
      }
      
      formData.append('messageType', messageType);

      // CRITICAL: Don't set Content-Type manually for FormData - browser needs to set it with boundary
      const headers = { ...this._getAuthHeaders() };
      // Remove Content-Type from headers - let browser set it automatically for FormData
      delete headers['Content-Type'];
      delete headers['content-type'];
      
      const response = await axios.post(`${this.apiUrl}/messages`, formData, {
        headers: headers,
        timeout: 60000, // 60 second timeout for large files
      });

      return response.data?.data || response.data;
    } catch (error) {
      // Properly handle axios errors with defensive checks
      console.error('❌ sendFileMessageViaAPI error:', {
        error: error,
        hasResponse: !!error.response,
        hasRequest: !!error.request,
        message: error.message,
        status: error.response?.status,
        responseData: error.response?.data
      });
      
      if (error.response) {
        // Server responded with error status
        const responseData = error.response.data || {};
        const errorMessage = responseData.message || 
                           responseData.error || 
                           (typeof responseData === 'string' ? responseData : null) ||
                           `Server error: ${error.response.status || 'Unknown'}`;
        const enhancedError = new Error(errorMessage);
        enhancedError.response = error.response;
        enhancedError.status = error.response.status;
        enhancedError.data = responseData;
        throw enhancedError;
      } else if (error.request) {
        // Request was made but no response received
        throw new Error('Network error: No response from server. Please check your connection.');
      } else {
        // Error setting up the request or unexpected error type
        const errorMsg = error?.message || String(error) || 'Failed to send file message';
        throw new Error(errorMsg);
      }
    }
  }

  /**
   * REST API: Get drivers by role (role IDs 4 and 6) for a specific agency
   * This API returns drivers and office admins for chat functionality
   * 
   * @param {string} agencyId - The agency ID to filter drivers
   * @returns {Promise<Array>} Array of driver objects with full_name, user_id, user_role, etc.
   */
  async getDriversByRole(agencyId) {
    try {
      if (!agencyId) {
        console.warn('⚠️ getDriversByRole: No agencyId provided');
        return [];
      }

      const url = `${this.apiUrl}/users/by-role`;

      const response = await axios.get(
        url,
        {
          params: { agencyId },
          headers: this._getAuthHeaders()
        }
      );

      // Handle different response formats
      if (Array.isArray(response.data)) {
        // Direct array response
        return response.data;
      } else if (response.data?.success && Array.isArray(response.data.data)) {
        // {success: true, data: [...]} format
        return response.data.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        // {data: [...]} format (without success field)
        return response.data.data;
      } else {
        console.warn('⚠️ getDriversByRole: Unexpected response format:', {
          responseData: response.data,
          responseDataType: typeof response.data,
          isArray: Array.isArray(response.data)
        });
        return [];
      }
    } catch (error) {
      return [];
    }
  }

  // Private helper methods
  _parseMessage(data) {
    if (typeof data === 'object' && data !== null) {
      // CRITICAL: Fix messageType for voice recordings - backend might send wrong type
      let messageType = data.messageType || 'text';
      const fileName = data.fileName || '';
      
      // If filename suggests audio but messageType is wrong, fix it
      if (fileName && (fileName.startsWith('voice_') || fileName.match(/\.(webm|m4a|ogg|wav|mp3)$/i))) {
        if (messageType !== 'audio' && messageType !== 'voice') {
          console.warn('⚠️ Backend sent wrong messageType for voice recording in _parseMessage, fixing:', {
            fileName: fileName,
            originalMessageType: messageType,
            correctedMessageType: 'audio'
          });
          messageType = 'audio';
        }
      }
      
      return {
        id: data._id || data.id,
        roomId: data.roomId,
        senderId: data.senderId,
        senderName: data.senderName,
        senderRole: data.senderRole,
        receiverId: data.receiverId,
        receiverName: data.receiverName,
        receiverRole: data.receiverRole,
        message: data.message,
        messageType: messageType, // Use corrected messageType
        fileUrl: data.fileUrl || null,
        fileName: data.fileName || null,
        fileType: data.fileType || null,
        fileSize: data.fileSize || null,
        duration: data.duration || null,
        isRead: data.isRead === true || data.isRead === 1,
        readAt: data.readAt ? new Date(data.readAt) : null,
        createdAt: data.createdAt ? new Date(data.createdAt) : new Date(),
        updatedAt: data.updatedAt ? new Date(data.updatedAt) : new Date(),
      };
    }
    throw new Error('Invalid message data');
  }

  _notifyMessageListeners(message) {
    // Notify room-specific listener
    const roomListener = this.messageListeners.get(message.roomId);
    if (roomListener) {
      roomListener(message);
    }

    // Notify all global listeners (for chat list updates)
    // Only notify if there's no room-specific listener to prevent duplicates
    // OR if we want both (list view + room view), we rely on components to deduplicate
    // For now, we notify both but components should handle deduplication
    this.globalMessageListeners.forEach(listener => {
      try {
        listener(message);
      } catch (error) {
        // Error in global message listener - silently handle
      }
    });
  }

  _notifyTypingListeners(data) {
    const roomId = data?.roomId;
    if (roomId) {
      const listener = this.typingListeners.get(roomId);
      if (listener) {
        listener(data);
      }
    }
  }

  _notifyOnlineStatusListeners(data) {
    // Ensure online status is properly set
    const statusData = {
      ...data,
      online: data.online === true || data.online === 1 || data.online === 'true'
    };
    this.onlineStatusListeners.forEach(listener => {
      try {
        listener(statusData);
      } catch (error) {
        console.error('Error in online status listener:', error);
      }
    });
  }

  _notifyMessageReadStatusListeners(data) {
    const roomId = data?.roomId;
    
    if (roomId) {
      const listener = this.messageReadStatusListeners.get(roomId);
      if (listener) {
        try {
          listener(data);
        } catch (error) {
          // Error in read status listener - silently handle
        }
      }
    }
  }

  _notifyRoomUpdateListeners(data) {
    this.roomUpdateListeners.forEach(listener => listener(data));
  }

  /**
   * Get full file URL from relative or partial URL
   * Converts relative URLs to full URLs with proper API path
   * @param {string} fileUrl - File URL (can be relative, partial, or full)
   * @param {string} roomId - Optional room ID to normalize URL format
   * @returns {string} Full file URL
   */
  getFullFileUrl(fileUrl, roomId = null) {
    if (!fileUrl) {
      return null;
    }

    // If already a full URL (http/https), return as-is
    if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
      return fileUrl;
    }

    // If blob URL or file:// URL, return as-is (for optimistic messages)
    if (fileUrl.startsWith('blob:') || fileUrl.startsWith('file://')) {
      return fileUrl;
    }

    // Get base URL from socket URL or API URL
    // Use environment variable or fallback to localhost
    const baseUrl = this.socketUrl || this.apiUrl?.replace('/api/chat', '') || 'http://localhost:8001';

    // Normalize fileUrl - ensure it starts with /
    let normalizedUrl = fileUrl.startsWith('/') ? fileUrl : `/${fileUrl}`;

    // If roomId is provided and URL doesn't already have roomId in path, normalize it
    if (roomId && !normalizedUrl.includes(`/api/chat/files/${roomId}/`)) {
      // Extract filename from URL
      const filename = normalizedUrl.split('/').pop();
      
      // Check if URL already has /api/chat/files/ pattern
      if (normalizedUrl.includes('/api/chat/files/')) {
        // Replace existing roomId if present, or add roomId
        normalizedUrl = `/api/chat/files/${roomId}/${filename}`;
      } else if (normalizedUrl.includes('/uploads/chat/')) {
        // Convert /uploads/chat/ to /api/chat/files/ format
        normalizedUrl = `/api/chat/files/${roomId}/${filename}`;
      } else {
        // Default: use /api/chat/files/{roomId}/{filename} format
        normalizedUrl = `/api/chat/files/${roomId}/${filename}`;
      }
    } else if (!normalizedUrl.startsWith('/api/chat/files/') && !normalizedUrl.startsWith('/uploads/chat/')) {
      // If no roomId provided and URL doesn't match known patterns, try to use as-is
      // or construct with filename only
      const filename = normalizedUrl.split('/').pop();
      normalizedUrl = `/api/chat/files/${filename}`;
    }

    // Construct full URL
    const fullUrl = `${baseUrl}${normalizedUrl}`;
    
    return fullUrl;
  }
}

// Export singleton instance
const chatService = new ChatService();
export default chatService;

